import s from './RestaurantMainCard.module.sass'
import { FC, useRef } from 'react'
import { Title } from '@shared/ui/Typography/Title'
import { Button } from '@shared/ui/Actions/Button'
import { Rating } from '@shared/ui/Feedback/Rating'
import { useBookingClick } from '@widgets/Restaurant/Booking/lib/useBookingClick'
import { isAfishaWidgetAvailable } from '@shared/lib/booking/domain/providerRegistry'
import { useAppSelector } from '@app/model/store/client'
import { Text } from '@shared/ui/Typography/Text'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'

import {
  sendAdvertisingRestAnalytic,
  TypeAdvertisingCardRestEvent,
} from '@shared/api/analytics/advertising/advertising'

import { useIntersectionAnalytic } from '@shared/lib/hooks/useIntersectionAnalytic'
import { TypeMappingRestOutput } from '@shared/api/middleware/mappingAPI/restaurant/type'
import { MainCard } from '@shared/ui/Layout/Cards/MainCard'
import { EGG } from '@shared/api/analytics'
import { PickAnalytic } from '@shared/api/analytics/types/types'
import { PromoBadgeCTA } from '@shared/ui/Feedback/PromoBadges/PromoBadgeCTA'
import { IconButton } from '@shared/ui/Actions/IconButton'
import IconLike from '@icons/icon-like.svg'
import { Favorite } from '@features/Favorite'
import { PriceRange } from '@shared/ui/Feedback/PriceRange'
import { PRICE_FILTER_TAGS } from '@features/Restaurant/RestaurantFilters/consts/filterTags'

interface Props {
  data: TypeMappingRestOutput
  analytic?: PickAnalytic<null, 'section_name' | 'section_index' | 'section_type'>
}

const RestaurantMainCard: FC<Props> = ({ data, analytic }) => {
  const { id, name, description, feedback, images, url, discounts, type, price } = data
  const stateUser = useAppSelector((state) => state.auth.user.data)
  const handleBookingClick = useBookingClick({ restData: data })
  const thisCoverImage = images.cover.rest1064x1064 || images.cover.rest1894x1000
  const wrapperRef = useRef(null)
  const alt = `Фото ресторана: ${data.name}`
  const restType = type || 'Ресторан'
  const titleLength = name?.length ?? 0
  const priceLevel = PRICE_FILTER_TAGS.findIndex((tag) => tag.id === price?.tag?.id) + 1

  const handleBooking = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    handleBookingClick()
    EGG.entity.restaurant.click_booking(data, { section_name: 'feature', section_type: 'main_slider', ...analytic })
  }

  const handleAnalyticClickHouse = (typeEvent: 'click' | 'visible') => {
    const EGGMethod = typeEvent === 'visible' ? EGG.entity.restaurant.card_view : EGG.entity.restaurant.card_click

    EGGMethod(data, { section_name: 'feature', ...analytic })
  }

  const handleAnalyticAdvert = (typeEvent: TypeAdvertisingCardRestEvent) => {
    sendAdvertisingRestAnalytic({
      type: typeEvent,
      city_id: data.address.city.id,
      rest_id: id,
      variety: 'feature',
    })
  }

  useIntersectionAnalytic(wrapperRef, () => {
    handleAnalyticAdvert('visible')
    handleAnalyticClickHouse('visible')
  })

  const handleClickAnalytic = () => {
    handleAnalyticAdvert('click')
    handleAnalyticClickHouse('click')
  }

  const isActiveFavoriteIcon = Boolean(stateUser && data.id in (stateUser.favorites_objects?.restaurants ?? {}))

  return (
    <BlockBoundary>
      <MainCard
        img={thisCoverImage}
        alt={alt}
        href={url.path}
        onClick={handleClickAnalytic}
        classNameContent={s.card}
        layout='feature'>
        {feedback.rating >= 4 && <Rating rating={feedback.rating} className={s.rating} />}

        <div className={s.content} ref={wrapperRef}>
          <div className={s.meta}>
            <Text sizes={'S'} uppercase className={s.tag}>
              {restType}
            </Text>
            {priceLevel > 0 ? (
              <>
                <span className={s.dot} />
                <PriceRange priceRange={priceLevel} colorMode='white' />
              </>
            ) : null}
          </div>

          {name && (
            <Title
              tag='h2'
              sizes='h3'
              className={s['rest-title']}
              data-compact-mobile={titleLength > 22}
              data-compact-desktop={titleLength > 40}>
              {name}
            </Title>
          )}

          {description.title && (
            <Text sizes={'M S'} className={s['rest-description']}>
              {description.title}
            </Text>
          )}
        </div>

        {isAfishaWidgetAvailable(data?.booking) && (
          <div className={s['buttons-bottom']}>
            <Button
              sizes={'XL M'}
              data-temp-rest-id={id}
              mode={'white'}
              className={s['booking-button']}
              onClick={handleBooking}>
              Забронировать
            </Button>

            {discounts.remarked && (
              <PromoBadgeCTA sizes={'XL none'} className={s['promo-badge']}>
                -{discounts.remarked}₽
              </PromoBadgeCTA>
            )}

            {discounts.restoplace && (
              <PromoBadgeCTA sizes={'XL none'} className={s['promo-badge']}>
                -{discounts.restoplace}₽
              </PromoBadgeCTA>
            )}
          </div>
        )}

        <Favorite
          category={'restaurant'}
          id={data.id}
          analytic={{ data, other: { section_name: analytic?.section_name } }}
          className={s.favorite}>
          <IconButton
            sizes={'XL M'}
            mode={'transparent'}
            data-active={isActiveFavoriteIcon}
            icon={IconLike}
            className={s['favorite-icon']}
          />
        </Favorite>
      </MainCard>
    </BlockBoundary>
  )
}

export default RestaurantMainCard
